<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import UpdateBanner from '@/components/common/UpdateBanner.vue'
import ToastContainer from '@/components/common/ToastContainer.vue'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'

watchEffect(() => {
  document.title = `Home ${__APP_VERSION__}`
})

const route = useRoute()
const auth = useAuthStore()
const dashboard = useDashboardStore()
const safeAreaInsetTop = 'env(safe-area-inset-top, 0px)'
// Public share pages render without app chrome, even for signed-in users.
const showAppChrome = computed(() => auth.isAuthenticated && route.meta.public !== true)

const mainMarginClass = computed(() => {
  if (!showAppChrome.value) return ''
  const isDashboard = route.name === 'dashboard'
  const isDetail = route.name === 'detail'
  const isInventoryRoute = route.path.startsWith('/inventory')
  const isServerRoute = route.path.startsWith('/server')
  const hasLocations = dashboard.locations.length > 0
  const hasGroups = dashboard.locationGroups.length > 0
  if ((isDashboard || isDetail) && hasLocations && hasGroups) return 'mt-[8.5em] mb-[3em]'
  if ((isDashboard || isDetail) && hasLocations) return 'mt-[5.5em] mb-[3em]'
  if (isInventoryRoute) return 'mt-[5.5em] mb-[3em]'
  if (isServerRoute) return 'mt-[5.5em] mb-[3em]'
  return 'mt-[3em] mb-[3em]'
})

const mainOffsetStyle = computed(() => {
  if (!showAppChrome.value) {
    return undefined
  }

  const isDashboard = route.name === 'dashboard'
  const isDetail = route.name === 'detail'
  const isInventoryRoute = route.path.startsWith('/inventory')
  const isServerRoute = route.path.startsWith('/server')
  const hasLocations = dashboard.locations.length > 0
  const hasGroups = dashboard.locationGroups.length > 0

  if ((isDashboard || isDetail) && hasLocations && hasGroups) {
    return { marginTop: `calc(8.5em + ${safeAreaInsetTop})` }
  }

  if ((isDashboard || isDetail) && hasLocations) {
    return { marginTop: `calc(5.5em + ${safeAreaInsetTop})` }
  }

  if (isInventoryRoute) {
    return { marginTop: `calc(5.5em + ${safeAreaInsetTop})` }
  }

  if (isServerRoute) {
    return { marginTop: `calc(5.5em + ${safeAreaInsetTop})` }
  }

  return { marginTop: `calc(3em + ${safeAreaInsetTop})` }
})
</script>

<template>
  <div class="flex flex-col min-h-screen bg-aurora">
    <UpdateBanner />
    <AppHeader v-if="showAppChrome" />
    <main
      class="flex-1 w-full max-w-[2200px] mx-auto px-[0.5em]"
      :class="mainMarginClass"
      :style="mainOffsetStyle"
    >
      <router-view />
    </main>
    <AppFooter v-if="showAppChrome" />
    <ToastContainer />
  </div>
</template>
